import React, { useState, useEffect } from "react";
import { Steps, Row, Col, Grid } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setFormData,
  updateFormField,
  updateQuestionState,
} from "../../../store/slices/path.slice";
import { useQuestionConfiguration } from "./questionConfig";
import { CheckCircle, CircleDashed } from "lucide-react";
import {
  fetchPortfoliosInBatches,
  fetchDetailedPortfolioObjects,
} from "../../../api/portfolioApi";
import { useClients } from "../../Clients/useClients";
import PathSummary from "./PathSummary";
import useCompany from "../../Company/useCompany";
import AllQuestionsReviewStep from "./AllQuestionsReviewStep";
import useConfirmPathSubmission from "./useConfirmPathSubmission";
const { useBreakpoint } = Grid;

// Renders a single "normal" step question
const QuestionRenderer = ({ pathQuestions, questionId, ...props }) => {
  if (!pathQuestions) return null;
  const questionConfig = pathQuestions[questionId];
  if (!questionConfig?.component) return null;

  const Component = questionConfig.component;
  return <Component {...props} />;
};

/**
 * Custom hook for PathStepper logic
 * (Fetching questions, building steps, etc.)
 */
const usePathStepper = () => {
  const bp = useBreakpoint();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const [currentStep, setCurrentStep] = useState(0);
  const { companyDetails } = useCompany();
  const selectedPathId = params.pathId;
  const { confirmSubmission } = useConfirmPathSubmission();


  // Our main "step by step" state
  const [state, setState] = useState({
    targetAllocation: 60,
    incomeFocus: null,
    useGlobal: null,
    includeGold: null,
    includeBitcoin: null,
    region: [],
    leanPortfolio: null,
  });

  const questionStates = useSelector((s) => s.path.questionStates);

  // We'll define our finish callback here
  const handleFinish = () => {
    console.log('handleFinish called');
    dispatch(setFormData(state));
    confirmSubmission();
  };


  // Bring in the question config hook
  const { generatePathQuestions, isLoading, error } = useQuestionConfiguration(
    "portfolioPathQuestions",
    currentStep,
    setCurrentStep,
    handleFinish
  );

  // Possibly load the path object
  const pathObject = useSelector((s) =>
    s.user.data?.path_details.find((x) => x.id === selectedPathId)
  );
  const activePathIds = companyDetails?.active_path_ids ?? [];
  const orderedPaths = companyDetails?.path_pages?.filter((x) =>
    activePathIds.includes(x.id)
  );

  // On mount, fetch relevant portfolios
  useEffect(() => {
    if (pathObject?.portfolio_ids?.length > 0) {
      dispatch(
        fetchPortfoliosInBatches({ portfolioIds: pathObject.portfolio_ids })
      ).then(() => {
        dispatch(fetchDetailedPortfolioObjects(pathObject.portfolio_ids));
      });
    }
  }, [pathObject, dispatch]);

  // 1) Build question objects & steps from config
  const [pathQuestions, pathSteps] = generatePathQuestions({
    customPathPages: orderedPaths,
  });

  // 2) Add final "allQuestionsView" step if missing
  if (!pathSteps.find((s) => s.id === "allQuestionsView")) {
    pathSteps.push({ id: "allQuestionsView", title: "Review All" });
  }

  // 3) If that step config doesn't exist, create it
  if (!pathQuestions["allQuestionsView"]) {
    pathQuestions["allQuestionsView"] = {
      title: "Review All Questions",
      component: (props) => (
        <AllQuestionsReviewStep
          {...props}
          state={state}
          setState={setState}
          handleFinish={handleFinish}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          dispatch={dispatch}
          pathQuestions={pathQuestions}
        />
      ),
    };
  }

  // 4) Step icon logic
  const getQuestionState = (questionId) => {
    const currentStateQuestion = questionStates?.[questionId];
    if (!currentStateQuestion) return { completed: false, hasComponent: false };
    return currentStateQuestion;
  };

  const getStepIcon = (index, questionId) => {
    const { completed } = getQuestionState(questionId);
    if (index < currentStep) {
      return completed ? (
        <CheckCircle
          className="bg-emerald-300 rounded-full p-2"
          color="#fff"
          size={30}
        />
      ) : (
        <CircleDashed
          className="bg-gray-200 rounded-full p-2"
          color="#666"
          size={30}
        />
      );
    }
    return null;
  };

  // 5) For normal single-step rendering
  const getCurrentQuestionProps = () => {
    const step = pathSteps[currentStep];
    if (!step) return null;
    const questionId = step.id;

    return {
      pathQuestions,
      questionId,
      // The "value" is in our main step-based state
      value: state[questionId],
      onChange: (newVal) => {
        // Update local
        setState((prev) => ({ ...prev, [questionId]: newVal }));
        // And update Redux
        dispatch(updateFormField({ field: questionId, value: newVal }));
        dispatch(
          updateQuestionState({
            questionId,
            completed: true,
            hasComponent: true,
            value: newVal,
          })
        );
      },
    };
  };

  return {
    bp,
    currentStep,
    setCurrentStep,
    pathSteps,
    pathQuestions,
    getStepIcon,
    getCurrentQuestionProps,
    state,
    isLoading,
    error,
  };
};

/**
 * Main PathStepper Component
 */
const PathStepper = () => {
  const {
    bp,
    currentStep,
    setCurrentStep,
    pathSteps,
    pathQuestions,
    getStepIcon,
    getCurrentQuestionProps,
    state,
    isLoading,
    error,
  } = usePathStepper();

  // Handle loading / error in the component
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading questions...
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        Error loading questions
      </div>
    );
  }

  return (
    <div
      style={{ display: "flex", flexDirection: "column", height: "95vh" }}
      className="overflow-hidden"
    >
      <Row style={{ flex: 1 }} wrap={false}>
        <Col flex="1">
          <Steps
            className="p-3 border-b-2 border-gray-100"
            current={currentStep}
            items={pathSteps.map((item, index) => ({
              title: (
                <div
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                    fontSize: "12px",
                  }}
                >
                  {item.title}
                </div>
              ),
              onClick: () => setCurrentStep(index),
              icon: getStepIcon(index, item.id),
            }))}
          />

          {/* Normal single-step or final "review all" step */}
          <QuestionRenderer {...getCurrentQuestionProps()} />
        </Col>

        {/* Right-side summary on XXL screens */}
        {bp.xxl && (
          <Col
            flex="0 0 300px"
            style={{ padding: "16px" }}
            className="max-h-[95vh] border-l-2 border-gray-100"
          >
            <PathSummary state={state} setCurrentStep={setCurrentStep} />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default PathStepper;
